import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useDeleteClaimAttachmentMutation, useLazyGetClaimAttachmentQuery } from '../../../../data/claim';
import { isRequired } from '../../../../utils/validationUtils';
import { SLICES } from '../../../../constants/slices';

class CustomFile extends File {
  constructor(fileParts, fileName, options, id) {
    super(fileParts, fileName, options);
    this.id = id;
  }
}

export const useFileUpload = ({ incomingFiles, formKey, requiredErrorMessage }) => {
  const location = useLocation();
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);

  const formContext = useFormContext();
  const customerClaimId = useSelector((state) => state?.[SLICES.CLAIMS]?.customerClaimId);

  const [getAttachment] = useLazyGetClaimAttachmentQuery();
  const [deleteAttachment] = useDeleteClaimAttachmentMutation();

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      const fetchedFiles = [];

      await Promise.all(
        incomingFiles?.map(async (item) => {
          const path = item.DocumentPath.split('/').reverse().join('/');

          try {
            const fileData = await getAttachment(path).unwrap();
            const file = new CustomFile(
              [fileData],
              item.FileName,
              { type: fileData.type || 'application/octet-stream' },
              item.ClaimFileId
            );
            fetchedFiles.push(file);
          } catch {
            // Error handler
          }
        })
      );

      setFiles(fetchedFiles);
      setLoading(false);
    };

    if (incomingFiles && location.state) {
      fetchFiles();
    }
  }, [incomingFiles, getAttachment, location]);

  useEffect(() => {
    formContext.setValue(formKey, files);
  }, [files, formContext, formKey]);

  const validationError = () => {
    if (files.length === 0) {
      return isRequired(requiredErrorMessage);
    }
    return {};
  };

  const handleRemoveFile = (fileToRemove) => {
    const updatedFiles = files.filter((file) => file.name !== fileToRemove.name);
    formContext.setValue(formKey, updatedFiles);
    formContext.clearErrors(formKey);
    setFiles(updatedFiles);
    if (customerClaimId) {
      deleteAttachment(fileToRemove.id);
    }
  };

  const handleFileChange = (event) => {
    const newFiles = event?.target?.files || event?.dataTransfer?.files;
    const updated = [...files, ...newFiles];
    formContext.setValue(formKey, updated);
    setFiles(updated);
  };

  return {
    files,
    loading,
    validationError,
    handleRemoveFile,
    handleFileChange,
    formContext
  };
};
